import { REFI_MFE_UUID, RefiHttpClient } from '@cof/refi-web-sdk';
import { REFI_LIENHOLDER_CONST } from '../constants/app.constants';
import { SessionService } from './session.service';
import { DebugService } from '@services/debug.service';

const POST_APP_UPSTREAM_ENV = 'gen3';

class _ApiService {
  _client: any;
  _test: { hostAppNavigation: boolean };
  debug: DebugService;
  fetchWrapper: any;
  headers: any;
  subscribers: any;
  constructor() {
    this._client = new RefiHttpClient().withRetry(3);
    this.headers = { ...REFI_LIENHOLDER_CONST.HTTP_HEADERS };
    if (REFI_LIENHOLDER_CONST.isDevEnv) {
      this.headers['x-upstream-env'] = 'dev';
    }
    if (sessionStorage.getItem(REFI_LIENHOLDER_CONST.C1_REFI_APPIDS)) {
      this.headers[REFI_LIENHOLDER_CONST.C1_REFI_APPIDS] =
        sessionStorage.getItem(REFI_LIENHOLDER_CONST.C1_REFI_APPIDS);
    }
    this.debug = new DebugService('ApiService');

    this.subscribers = [];
    this._test = {
      hostAppNavigation: true,
    };

    this.fetchWrapper = (url: string, opts: RequestInit, timeout = 0) => {
      this.debug.log(
        `${timeout <= 0 ? 'Not' : ''} using custom timeout${
          timeout > 0 ? ` of ${timeout}ms` : ''
        } for url ${url}`
      );
      return timeout > 0
        ? this._client.withCustomTimeout(this._client.fetch(url, opts), timeout)
        : this._client.fetch(url, opts);
    };
  }

  setUpstreamHeader(value: string) {
    this.headers['x-upstream-env'] = value;
  }

  set test({ hostAppNavigation = true }) {
    this.debug.log('setting test with api service with', { hostAppNavigation });
    this._test = {
      hostAppNavigation,
    };
    this.publish();
  }

  buildHeaders(
    additionalHeaders: Record<string, string> = {}
  ): Record<string, string> {
    const id = REFI_MFE_UUID.makeUUID();
    const headers = {
      ...this.headers,
      ...additionalHeaders,
      'Client-Correlation-Id': id,
    };

    if (SessionService.session?.eAppId) {
      headers.eapp_id = SessionService.session.eAppId;
    }

    headers['x-upstream-env'] = POST_APP_UPSTREAM_ENV;
    return headers;
  }

  async get(url: string, opts: RequestInit = {}, timeout = 0) {
    const headers = this.buildHeaders(
      opts.headers as Record<string, string> | undefined
    );
    return this.fetchWrapper(
      url,
      {
        ...opts,
        headers,
        withCredentials: true,
      },
      timeout
    ).then(_ApiService._httpInterceptor);
  }

  async post(url: string, body: any, opts: RequestInit, timeout = 0) {
    const headers = this.buildHeaders(
      opts?.headers as Record<string, string> | undefined
    );
    const options = {
      ...opts,
      headers,
      withCredentials: true,
      method: 'POST',
      body: JSON.stringify(body),
    };
    return this.fetchWrapper(url, options, timeout).then(
      _ApiService._httpInterceptor
    );
  }

  static _httpInterceptor(res: any) {
    if (res.status === 204) {
      return res;
    }
    if (res.status >= 200 && res.status < 300) {
      return res.json();
    }
    if (res.status === 403) {
      document.dispatchEvent(new CustomEvent('refi-login-sign-out'));
    }
    if (res.status === 401) {
      document.dispatchEvent(new CustomEvent('refi-login-sign-out'));
    }
    return Promise.reject(res);
  }

  publish() {
    this.subscribers.forEach((subscriber: any) => {
      subscriber.callback(this._test);
    });
  }

  subscribe(name: string, callback: any) {
    const index = this.subscribers.findIndex(
      (subscriber: Subscriber) => subscriber.name === name
    );
    if (index > -1) {
      this.subscribers[index] = { name, callback };
    } else {
      this.subscribers.push({ name, callback });
    }
    callback(this._test);
  }

  unsubscribe(name: string) {
    const index = this.subscribers.findIndex(
      (subscriber: any) => subscriber.name === name
    );
    if (index > -1) {
      this.subscribers.splice(index, 1);
    }
  }
}

export const ApiService = new _ApiService();

type Subscriber = {
  name: string;
  callback: any;
};
